import React, { useEffect, useState } from 'react'
import i18next from 'i18next'
import { I18nextProvider, initReactI18next } from 'react-i18next'
import Spinner from '../components/LoginForm/Spinner'
import { type SupportedGeo, type SupportedLanguage } from '../geo/config'
import { useGeoRuntime } from '../geo/useGeoRuntime'

type TranslationValue = string | TranslationTree

interface TranslationTree {
  [key: string]: TranslationValue
}

const LOCALE_FILE_MAP: Partial<Record<SupportedLanguage, string>> = {
  ky: 'kg',
}

const localeCache = new Map<string, Promise<TranslationTree>>()

const i18n = i18next.createInstance()
const i18nInitPromise = i18n.use(initReactI18next).init({
  defaultNS: 'translation',
  fallbackLng: 'ru',
  interpolation: {
    escapeValue: false,
  },
  lng: 'ru',
  ns: ['translation'],
  resources: {},
})

const isRecord = (value: TranslationValue | undefined): value is TranslationTree =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const getByPath = (value: TranslationTree, key: string): TranslationValue | undefined => {
  return key.split('.').reduce<TranslationValue | undefined>((acc, segment) => {
    if (!isRecord(acc)) return undefined

    return acc[segment]
  }, value)
}

const mergeDeep = (target: TranslationTree, source: TranslationTree) => {
  Object.entries(source).forEach(([key, value]) => {
    if (isRecord(value) && isRecord(target[key])) {
      mergeDeep(target[key], value)
      return
    }

    target[key] = value
  })

  return target
}

const loadLocale = async (language: SupportedLanguage) => {
  const fileName = LOCALE_FILE_MAP[language] ?? language
  const cacheKey = `/locales/${fileName}.json`

  if (!localeCache.has(cacheKey)) {
    localeCache.set(
      cacheKey,
      fetch(cacheKey).then(async (response) => {
        if (!response.ok) {
          throw new Error(`Failed to load locale: ${cacheKey}`)
        }

        return (await response.json()) as TranslationTree
      }),
    )
  }

  return localeCache.get(cacheKey)!
}

const applyGeoVariant = (translations: TranslationTree, geo: SupportedGeo) => {
  const clonedTranslations = JSON.parse(JSON.stringify(translations)) as TranslationTree
  const variant = getByPath(translations, `variants.${geo}`)

  if (isRecord(variant)) {
    mergeDeep(clonedTranslations, variant)
  }

  // if ('variants' in clonedTranslations) {
  //   delete clonedTranslations.variants
  // }

  return clonedTranslations
}

export const TranslationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { geo, language } = useGeoRuntime()
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    let isActive = true

    Promise.all([i18nInitPromise, loadLocale(language), loadLocale('ru')])
      .then(([, currentTranslations, fallbackTranslations]) => {
        if (!isActive) return

        i18n.addResourceBundle(
          language,
          'translation',
          applyGeoVariant(currentTranslations, geo),
          true,
          true,
        )
        i18n.addResourceBundle(
          'ru',
          'translation',
          applyGeoVariant(fallbackTranslations, geo),
          true,
          true,
        )

        return i18n.changeLanguage(language)
      })
      .then(() => {
        if (isActive) {
          setIsReady(true)
        }
      })
      .catch((error) => {
        console.error(error)
        if (isActive) {
          setIsReady(true)
        }
      })

    return () => {
      isActive = false
    }
  }, [geo, language])

  if (!isReady) {
    return <Spinner />
  }

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
}